import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Responsive from '../../../assets/config/Responsive'
import StarIcon from '../../../assets/v2/JSX/StarIcon'
import { useTranslation } from 'react-i18next'
import Colors from '../../../assets/styles/Colors'
import debounce from 'lodash/debounce'
import DownTriangleIcon from '../../../assets/v2/JSX/DownTriangleIcon'

const PriceAccordion: React.FC<{
  isBestPrice: boolean
  children?: React.ReactNode
  isSelected?: boolean
  imgUrl?: string
  headTitle?: string
  isDisabled?: boolean
  defaultIsOpen?: boolean
  price?: string
  addonsPrice?: string
  docUrl?: string
  onClick?: () => void
}> = ({
  isBestPrice = false,
  children,
  isSelected = false,
  imgUrl = '',
  headTitle = '',
  isDisabled = false,
  defaultIsOpen = false,
  addonsPrice = '',
  price,
  docUrl,
  onClick,
}) => {
  //
  const { t } = useTranslation()
  //
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  //
  const [isOpen, setIsOpen] = useState<boolean>(defaultIsOpen)
  //
  useEffect(() => {
    const handleResize = debounce(() => setScreenWidth(window.innerWidth), 100)

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])
  //
  //
  return (
    <Wrapper $isBestPrice={isBestPrice}>
      {isBestPrice ? (
        <BestPrice>
          <StarIcon />
          <span>{`${t('bestOffer')}`}</span>
        </BestPrice>
      ) : null}
      <Head
        style={{
          marginBottom: screenWidth <= 1199 ? (isOpen ? '24px' : '0px') : '28px',
        }}
        onClick={() => {
          if (screenWidth <= 1199) {
            setIsOpen(!isOpen)
          } else {
            setIsOpen(!isOpen)
          }
        }}
      >
        <ArrowBtn
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            opacity: screenWidth <= 1199 ? 'unset' : '0',
            userSelect: screenWidth <= 1199 ? 'unset' : 'none',
            pointerEvents: screenWidth <= 1199 ? 'unset' : 'none',
            width: screenWidth <= 1199 ? 'unset' : '0px',
          }}
        >
          <DownTriangleIcon />
        </ArrowBtn>

        <CompanyInfo $isDisabled={isDisabled}>
          <img src={imgUrl} alt={`${imgUrl}-alt`} />
          <span>{headTitle}</span>
        </CompanyInfo>

        <div></div>
      </Head>

      <RadioButtonStyle>
        <CircleStatus onClick={onClick} $selected={isSelected} />
      </RadioButtonStyle>

      <Content
        style={{
          display: screenWidth <= 1199 ? (isOpen ? 'unset' : 'none') : 'unset',
        }}
      >
        {children}
      </Content>

      <HR />

      <AlwaysVisible>
        <DetailDiv>
          <PriceDiv>{price}</PriceDiv>

          <ATagStyle href={docUrl} target="_blank">
            <div>{t('policyTerms')}</div>
          </ATagStyle>
        </DetailDiv>
        {addonsPrice && (
          <AddonsPrice>
            <b>+ {addonsPrice}</b> {t('additionalCoverages')}
          </AddonsPrice>
        )}
      </AlwaysVisible>
    </Wrapper>
  )
}
//
//
const AddonsPrice = styled.div`
  font-size: 14px;
  color: ${Colors.nevadaGrey};
  text-align: left;
  margin-top: 2px;
  white-space: nowrap;

  b {
    color: ${Colors.primaryRed};
  }
`

const PriceDiv = styled.div`
  font-size: 28px;
  color: ${Colors.primaryBlack};
  text-decoration: none;
  font-weight: 700;
  letter-spacing: 2px;
  white-space: nowrap;

  ${Responsive.tablet} {
    font-size: 22px;
    line-height: 22px;
  }
  ${Responsive.mobile} {
    font-size: 22px;
    line-height: 22px;
  }
`
const ATagStyle = styled.a`
  font-size: 14px;
  line-height: 20px;
  color: ${Colors.primaryBlack};
  text-decoration: underline;
  text-align: right;

  /*  */
  &:hover {
    color: ${Colors.primaryBlack};
    div {
      svg {
        color: ${Colors.primaryRed};
      }
    }
  }
`
const HR = styled.hr`
  background-color: ${Colors.secondaryGrey};
  margin-block: 24px 27px;
`
const DetailDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const AlwaysVisible = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 56px;

  ${Responsive.tablet} {
    padding-bottom: 43px;
  }
  ${Responsive.mobile} {
    padding-bottom: 43px;
  }
`
const CompanyInfo = styled.div<{ $isDisabled: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  user-select: ${(props) => (props.$isDisabled ? 'none' : 'unset')};
  pointer-events: ${(props) => (props.$isDisabled ? 'none' : 'unset')};
  opacity: ${(props) => (props.$isDisabled ? '0.6' : 'unset')};

  /*  */
  img {
    height: 32px;
    margin-right: 10px;
  }
  /*  */
  span {
    font-size: 18px;
    color: ${Colors.primaryBlack};
  }

  /*  */
  ${Responsive.mobile} {
    img {
      height: 24px;
    }
  }
`
const RadioButtonStyle = styled.div`
  label {
    position: absolute;
    top: 16px;
    right: 16px;
  }

  ${Responsive.mobile} {
    label {
      top: 15px;
      right: 15px;
    }
  }
`
const CircleStatus = styled.div<{ $selected: boolean }>`
  z-index: 2;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  position: absolute;
  top: 15px;
  right: 15px;
  border: ${({ $selected }) => ($selected ? 'unset' : `solid 1px ${Colors.nevadaGrey}`)};
  box-shadow: ${({ $selected }) => ($selected ? `inset 0px 0px 0px 11px ${Colors.primaryRed}` : 'unset')};
  cursor: pointer;

  /*  */
  ${Responsive.mobile} {
    width: 26px;
    height: 26px;
    box-shadow: ${({ $selected }) => ($selected ? `inset 0px 0px 0px 9px ${Colors.primaryRed}` : 'unset')};
  }
`
const ArrowBtn = styled.button`
  outline: none;
  border: none;
  background-color: transparent;
  padding: 10px;
  margin: 0 0 0 -10px;
  height: 30px;
  width: 30px;
  display: grid;
  place-items: center;

  svg {
    width: 14px;
    height: 7px;
  }
`
const Content = styled.div`
  /*  */
`
const Head = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  ${Responsive.tablet} {
    justify-content: space-between;
  }
  ${Responsive.mobile} {
    justify-content: space-between;
  }
`
const BestPrice = styled.div`
  display: flex;
  align-items: center;
  background-color: ${Colors.lightRed};
  border-bottom-right-radius: 8px;
  height: 40px;
  width: fit-content;
  padding-inline: 24px;
  position: absolute;
  top: 0;
  left: 0;

  /*  */
  svg {
    height: 14px;
    width: 14.59px;
    color: ${Colors.primaryRed};
    stroke: ${Colors.primaryRed};
  }
  /*  */
  span {
    margin-left: 9px;
    font-size: 14px;
    color: ${Colors.primaryRed};
  }

  ${Responsive.tablet} {
    height: 32px;
    padding-inline: 14px;
  }
  ${Responsive.mobile} {
    height: 32px;
    padding-inline: 14px;
  }
`
const Wrapper = styled.div<{ $isBestPrice: boolean }>`
  width: 100%;
  position: relative;
  background-color: ${Colors.white};
  -webkit-box-shadow: 0px 0px 15px 3px #88888811;
  box-shadow: 0px 0px 15px 3px #88888811;
  border-radius: 8px;
  padding-inline: 48px;
  padding-top: ${({ $isBestPrice }) => ($isBestPrice === true ? `55px` : `48px`)};
  max-width: ${({ $isBestPrice }) => ($isBestPrice === true ? `504px` : `464px`)};

  ${Responsive.laptop} {
    padding-top: ${({ $isBestPrice }) => ($isBestPrice === true ? `50px` : `30px`)};
  }

  ${Responsive.tablet} {
    padding-top: ${({ $isBestPrice }) => ($isBestPrice === true ? `50px` : `40px`)};
    max-width: unset;
  }

  ${Responsive.mobile} {
    padding-top: ${({ $isBestPrice }) => ($isBestPrice === true ? `50px` : `28px`)};
    max-width: unset;
    padding-inline: 24px;
  }
`
//
export default PriceAccordion
